/**
 * Requester-scope permission helpers for service requests.
 *
 * Single source of truth for the "can the requester reassign this SR
 * right now?" question — used by every UI surface that renders the
 * Reassign button on the requester side (details modal today; anything
 * that ports the same button tomorrow).
 *
 * Predicate is (status × routing type × bounce marker) per product spec.
 * Backend truth cross-checked in rialty-user-service ServiceRequestService:
 *   - Fresh company routing:           status=AWAITING_ASSIGNMENT, previousProviderId=null.
 *   - Company internally assigned:     status=PENDING,             providerId=inspector.
 *   - Inspector-declined bounce:       status=AWAITING_ASSIGNMENT, previousProviderId=inspectorId.
 *                                       (applyRequestScope reflow, ~line 863)
 *   - Company itself declined:         status=DECLINED,            routedByCompanyId preserved.
 *                                       (companyDeclineRequest, ~line 1504)
 *
 * Rules:
 * - Open SR (no providerId AND no routedByCompanyId):
 *     ENABLED  in AWAITING_ASSIGNMENT — the requester created a placeholder
 *              without picking a provider up-front and is now filling it in.
 *              Backend accepts the PATCH providerId change via the same
 *              updateAsRequester path used by reassign. Introduced with the
 *              open-SR create flow 2026-09-08.
 * - Company-routed SR (routedByCompanyId set):
 *     ENABLED  iff status=AWAITING_ASSIGNMENT AND previousProviderId is null
 *              (fresh company routing — requester can retarget to a different vendor
 *              before the company picks an inspector). OR status=DECLINED (company
 *              itself said no — reopens per spec §2).
 *     LOCKED   in PENDING (company has picked an inspector; internal state, invisible
 *              lock per spec §2), and in bounced AWAITING_ASSIGNMENT
 *              (previousProviderId set — internal reshuffling; spec §2: "should not
 *              be visible to the requester and should not reopen reassign").
 * - Individual direct SR (no routedByCompanyId, providerId set): enabled in PENDING
 *     (pre-accept) and reopens in DECLINED (spec §1). Backend `updateAsRequester`
 *     guard was extended 2026-09-07 to accept DECLINED + providerId change.
 * - Post-accept (ACCEPTED / IN_PROGRESS / COMPLETED) and terminal
 *   CANCELLED / EXPIRED always lock.
 */

const POST_ACCEPT_STATUSES = new Set(['ACCEPTED', 'IN_PROGRESS', 'COMPLETED']);

/**
 * @param {{ status?: string, providerId?: string | null, routedByCompanyId?: string | null, previousProviderId?: string | null }} sr
 * @returns {{ enabled: boolean, disabledReason: string }}
 *   `disabledReason` is a state-specific tooltip / subtext string.
 *   Always non-empty so callers can safely render it in the disabled
 *   branch without falling back to a generic string.
 */
export function getRequesterReassignState(sr) {
  if (!sr) return { enabled: false, disabledReason: 'Reassign unavailable for this service request.' };
  const status = sr.status || '';
  const isCompanyRouted = !!sr.routedByCompanyId;
  const hasProvider = !!sr.providerId;
  const hasInspectorBounce = !!sr.previousProviderId;
  // Open-SR case: no provider ever picked, no company routing. The requester
  // is filling in an assignment on a placeholder they just created — always
  // enabled while the SR is still AWAITING_ASSIGNMENT.
  const isOpenAwaiting = !hasProvider && !isCompanyRouted
    && status === 'AWAITING_ASSIGNMENT';
  let enabled;
  if (isOpenAwaiting) {
    enabled = true;
  } else if (isCompanyRouted) {
    if (status === 'AWAITING_ASSIGNMENT') enabled = !hasInspectorBounce;
    else if (status === 'DECLINED') enabled = true;
    else enabled = false;
  } else {
    enabled = status === 'PENDING' || status === 'DECLINED';
  }
  if (enabled) return { enabled: true, disabledReason: '' };
  let disabledReason = 'Reassign unavailable for this service request.';
  if (POST_ACCEPT_STATUSES.has(status)) {
    disabledReason = 'Reassign unavailable — provider has accepted this job.';
  } else if (isCompanyRouted && status === 'PENDING') {
    disabledReason = 'Reassign unavailable — the company has assigned this to an inspector.';
  } else if (isCompanyRouted && status === 'AWAITING_ASSIGNMENT' && hasInspectorBounce) {
    disabledReason = 'Reassign unavailable — the company is reassigning within its team.';
  } else if (status === 'CANCELLED' || status === 'EXPIRED') {
    disabledReason = 'Reassign unavailable — this service request is ' + status.toLowerCase() + '.';
  }
  return { enabled: false, disabledReason };
}
